import { useState, useEffect } from 'react';
import { Users, UserCheck, CreditCard, ChevronRight, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui';
import { PnCReview } from './PnCReview';
import { BUReview } from './BUReview';
import { FinanceReview } from './FinanceReview';
import { useAuth } from '@/hooks/useAuth';

// Type for workflow steps
type WorkflowStep = 'pnc' | 'bu' | 'finance';

// Interface for user permissions
interface UserPermissions {
  canAccessPnC: boolean;
  canAccessBU: boolean;
  canAccessFinance: boolean;
  userRoles: string[];
}

export const HonorariumWorkflow = () => {
  const [activeStep, setActiveStep] = useState<WorkflowStep>('pnc');
  const [userPermissions, setUserPermissions] = useState<UserPermissions>({
    canAccessPnC: false,
    canAccessBU: false,
    canAccessFinance: false,
    userRoles: []
  });

  const { user, isAuthenticated, isLoading } = useAuth();

  // Debug logging
  useEffect(() => {
    console.log('HonorariumWorkflow - Auth State:', {
      user,
      isAuthenticated,
      isLoading,
      userRoles: user?.roles
    });
  }, [user, isAuthenticated, isLoading]);

  // Update permissions when user changes
  useEffect(() => {
    if (!user || !isAuthenticated) {
      console.log('HonorariumWorkflow - No user or not authenticated');
      return;
    }

    const roles = user.roles || [];
    const permissions: UserPermissions = {
      canAccessPnC: roles.some(role => role.startsWith('INVOICE.PNC') || role === 'ADMIN'),
      canAccessBU: roles.some(role => role.startsWith('INVOICE.BU') || role === 'ADMIN'),
      canAccessFinance: roles.some(role => role.startsWith('INVOICE.FINANCE') || role === 'ADMIN'),
      userRoles: roles
    };

    setUserPermissions(permissions);
    console.log('HonorariumWorkflow - Permissions set:', permissions);

    // Set default active step based on user permissions
    if (permissions.canAccessPnC) {
      setActiveStep('pnc');
    } else if (permissions.canAccessBU) {
      setActiveStep('bu');
    } else if (permissions.canAccessFinance) {
      setActiveStep('finance');
    }
  }, [user, isAuthenticated]);

  // Workflow steps configuration
  const workflowSteps = [
    {
      key: 'pnc' as WorkflowStep,
      title: 'PnC Review',
      description: 'Review attendance and approve for BU',
      icon: Users,
      color: 'blue',
      accessible: userPermissions.canAccessPnC
    },
    {
      key: 'bu' as WorkflowStep,
      title: 'BU Review',
      description: 'Update donor details and approve for Finance',
      icon: UserCheck,
      color: 'green',
      accessible: userPermissions.canAccessBU
    },
    {
      key: 'finance' as WorkflowStep,
      title: 'Finance Review',
      description: 'Download and process payments',
      icon: CreditCard,
      color: 'purple',
      accessible: userPermissions.canAccessFinance
    }
  ];

  // Loading state is handled by ProtectedRoute
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rgf-primary-600"></div>
      </div>
    );
  }

  // Check if user has any permissions
  const hasAnyPermissions = userPermissions.canAccessPnC || 
                           userPermissions.canAccessBU || 
                           userPermissions.canAccessFinance;

  if (!hasAnyPermissions) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Access Denied</h3>
          <p className="mt-2 text-sm text-gray-500">
            You don't have permission to access the honorarium management system. 
            Please contact your administrator for access.
          </p>
          <div className="mt-6">
            <Button
              onClick={() => window.location.href = '/'}
              variant="secondary"
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const renderStepComponent = () => {
    switch (activeStep) {
      case 'pnc':
        return <PnCReview />;
      case 'bu':
        return <BUReview />;
      case 'finance':
        return <FinanceReview />;
      default:
        return <div>Invalid step</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with workflow navigation */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Honorarium Management</h1>
                <p className="mt-2 text-sm text-gray-600">
                  Three-step approval workflow for honorarium processing
                </p>
              </div>
            </div>

            {/* Workflow Steps Navigation */}
            <div className="mt-8">
              <nav className="flex space-x-8" aria-label="Workflow Steps">
                {workflowSteps.map((step, index) => {
                  const isActive = activeStep === step.key;
                  const isAccessible = step.accessible;
                  const IconComponent = step.icon;

                  return (
                    <div key={step.key} className="flex items-center">
                      <button
                        onClick={() => isAccessible && setActiveStep(step.key)}
                        disabled={!isAccessible}
                        className={`
                          flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                          ${isActive && isAccessible
                            ? 'bg-rgf-primary-100 text-rgf-primary-700 border-2 border-rgf-primary-200'
                            : isAccessible
                            ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            : 'text-gray-400 cursor-not-allowed'
                          }
                        `}
                      >
                        <div className={`
                          flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center mr-3
                          ${isActive && isAccessible
                            ? `bg-${step.color}-500 text-white`
                            : isAccessible
                            ? `bg-${step.color}-100 text-${step.color}-600`
                            : 'bg-gray-100 text-gray-400'
                          }
                        `}>
                          <IconComponent className="h-5 w-5" />
                        </div>
                        <div className="text-left">
                          <div className="font-medium">{step.title}</div>
                          <div className="text-xs opacity-75">{step.description}</div>
                        </div>
                        {!isAccessible && (
                          <div className="ml-2">
                            <AlertCircle className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                      </button>
                      
                      {index < workflowSteps.length - 1 && (
                        <ChevronRight className="flex-shrink-0 mx-2 h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderStepComponent()}
      </div>

      {/* User Permissions Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white p-4 rounded-lg text-xs max-w-xs">
          <div className="font-medium mb-2">Debug: User Permissions</div>
          <div>PnC: {userPermissions.canAccessPnC ? '✅' : '❌'}</div>
          <div>BU: {userPermissions.canAccessBU ? '✅' : '❌'}</div>
          <div>Finance: {userPermissions.canAccessFinance ? '✅' : '❌'}</div>
          <div className="mt-2 text-xs opacity-75">
            Roles: {userPermissions.userRoles.join(', ')}
          </div>
        </div>
      )}
    </div>
  );
};